import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  IMarketArticlesTileBasePayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import isEqual from 'lodash/isEqual';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import { adaptFilters } from '@/creditflowresearch/application/ArticlesAndStats/Articles/serverSide/filterAdapter';
import {
  buildIndustryFilters,
  buildSectorFilters,
} from '@/creditflowresearch/application/helpers/buildFilters';
import { buildCompanyArticlesFilters } from '@/creditflowresearch/application/ArticlesAndStats/Articles/ArticlesTile/helpers';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import Market from '@/creditflowresearch/domain/Market';
import Segment from '@/creditflowresearch/domain/Segment';

export const useArticlesPayload = (initPayload) => {
  const [articlesPayload, setArticlesPayload] = useState<IMarketArticlesTileBasePayload>(initPayload);
  const { query: {
    sponsorName,
    issuerName,
    products,
    regions,
    markets,
    segments,
  } } = useRouter();
  const { pageFilters: { industry, sector, includedParentId } } = usePageFiltersContext();
  const { deal } = useParentPageContext();
  const { sectorId, subsectorId } = sector || {};
  const { industryId, subindustryId } = industry || {};

  const computedPayloadFilter = useMemo(() => {
    if (deal && deal.id) {
      return {
        includedDealIds: [deal.id],
      };
    }
    const filters = {
      includedProductIds: Product.getIds(products),
      includedRegionIds: Region.getIds(regions),
      includedMarketIds: Market.getIds(markets),
      includedSegmentIds: Segment.getIds(segments),
      ...buildCompanyArticlesFilters(includedParentId, sponsorName as string, issuerName as string),
      ...buildSectorFilters(sectorId, subsectorId),
      ...buildIndustryFilters(industryId, subindustryId),
    };
    return adaptFilters(filters, { markets, segments });
  }, [
    articlesPayload.filter.includedDealIds,
    regions,
    products,
    markets,
    segments,
    includedParentId,
    sponsorName,
    issuerName,
    sectorId,
    subsectorId,
    industryId,
    subindustryId,
  ]);

  useEffect(() => {
    setArticlesPayload((prevState) => {
      if (isEqual(prevState.filter, computedPayloadFilter)) {
        return prevState;
      }
      return {
        ...prevState,
        filter: computedPayloadFilter,
      };
    });
  }, [computedPayloadFilter]);

  return articlesPayload;
};
